import React, { useState, useEffect, useCallback } from 'react';
import {
    UserCheck, Tablet as IconaTablet, AlertTriangle, Check, X, Pencil, RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import RicercaComune from '../components/RicercaComune';
import '../styles/AccettazioniTablet.css';
import CampoDocumento from '../components/CampoDocumento';
import CampoDataNascita from '../components/CampoDataNascita';
import AvvisoCap from '../components/AvvisoCap';
import { useSporco, useUscitaProtetta } from '../utils/uscitaProtetta';

/**
 * AccettazioniTablet — i dati compilati dai pazienti sul tablet,
 * in attesa di verifica.
 *
 * Nulla entra in anagrafica finche' qualcuno non guarda: due persone
 * che sbagliano il codice fiscale creerebbero altrimenti due schede
 * da ripulire a mano.
 *
 * Quando il codice fiscale corrisponde a un paziente gia' in archivio
 * la riga lo segnala: alla conferma si aggiorna quello, non se ne crea
 * un altro.
 */

const dataOra = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleString('it-IT', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
    });
};

const dataIta = (v) => {
    if (!v) return '';
    const d = new Date(v);
    return isNaN(d) ? '' : d.toLocaleDateString('it-IT');
};

const messaggio = (e, ripiego) =>
    e?.response?.data?.message || e?.message || ripiego;

const CAMPI = [
    ['cognome', 'Cognome'], ['nome', 'Nome'], ['sesso', 'Sesso'],
    ['data_nascita', 'Data di nascita'], ['luogo_nascita', 'Comune di nascita'],
    ['provincia_nascita', 'Prov. nascita'],
    /* Sta accanto alla provincia di nascita perche' sono alternativi: chi
       ha uno stato estero non ha una provincia italiana. Senza questo
       campo la segreteria correggeva la residenza qui e la nascita
       aprendo la scheda paziente - cioe' proprio il giro che questa coda
       esiste per evitare. */
    ['stato_nascita', 'Stato di nascita'],
    ['codice_fiscale', 'Codice fiscale'],
    ['via', 'Via'], ['civico', 'Civico'], ['citta', 'Città'],
    ['provincia', 'Provincia'], ['cap', 'CAP'],
    /* Chi risiede all'estero non ha provincia ne' CAP italiani: senza
       questo campo la segreteria non vedeva nemmeno cosa aveva dichiarato
       al tablet, e non poteva correggerlo prima di confermare. */
    ['stato_residenza', 'Stato di residenza'],
    ['telefono', 'Telefono'], ['email', 'Email'],
];

const AccettazioniTablet = () => {
    const [anagrafiche, setAnagrafiche] = useState([]);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);
    const [aperta, setAperta] = useState(null);
    const [modifiche, setModifiche] = useState({});
    const [inCorso, setInCorso] = useState(false);

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            const r = await api.get('/firma-tablet/anagrafiche', {
                /* Due stati insieme: le accettazioni ancora da guardare e
                   quelle il cui paziente è stato creato ma senza tutti i
                   moduli firmati. Sono cose diverse e si guardano nello
                   stesso momento — la seconda, se non comparisse qui, non
                   comparirebbe da nessuna parte. */
                params: { stato: 'in_attesa,archiviazione_non_riuscita' }
            });
            setAnagrafiche(r.data.data.anagrafiche || []);
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile caricare le accettazioni'));
        } finally {
            setCaricamento(false);
        }
    }, []);

    useEffect(() => { carica(); }, [carica]);

    const apri = (a) => {
        setAperta(a);
        setModifiche({});
        setErrore(null);
    };
    /* Le correzioni fatte nella finestra (modifiche) si perdono chiudendo:
       sfondo, X e «Chiudi» chiedono conferma se ce ne sono. */
    const sporco = useSporco(aperta ? modifiche : null, 0);
    const chiedi = useUscitaProtetta(sporco);
    const chiudi = () => { if (chiedi()) setAperta(null); };

    const valore = (campo) =>
        modifiche[campo] !== undefined ? modifiche[campo] : (aperta?.[campo] || '');

    /* Con una pratica completa e un codice fiscale già in archivio, creare
       un paziente nuovo non è possibile: il server rifiuta, perché
       vorrebbe dire sovrapporre quattro consensi a quelli già firmati.
       L'unica mossa sensata è archiviare i moduli sulla scheda che c'è.
       Se invece la segreteria corregge il codice fiscale — perché quello
       scritto al tablet era sbagliato — la collisione non c'è più e si
       torna a creare: per questo la scelta guarda anche le correzioni,
       non solo il dato arrivato. */
    const cfCambiato = modifiche.codice_fiscale !== undefined
        && String(modifiche.codice_fiscale).toUpperCase().trim()
           !== String(aperta?.codice_fiscale || '').toUpperCase().trim();
    const suEsistente = Boolean(aperta?.esistente_id)
        && Boolean(aperta?.pratica_completa) && !cfCambiato;

    /* Per queste righe il paziente ESISTE GIÀ: correggere i dati qui non
       lo cambierebbe, e «Conferma» non ha più senso. L'unica mossa è
       riprovare l'archiviazione dei moduli che mancano. */
    const daRiarchiviare = aperta?.stato === 'archiviazione_non_riuscita';

    const riprova = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            const r = await api.post(
                `/firma-tablet/anagrafiche/${aperta.id}/riprova-archiviazione`);
            setAvviso(r.data.message);
            setAperta(null);
            await carica();
        } catch (e) {
            /* Il 409 non è un guasto del programma: vuol dire che i moduli
               non si sono archiviati nemmeno adesso, e il server dice
               quali. La finestra resta aperta, perché la riga non è
               cambiata di stato e c'è ancora qualcosa da fare. */
            setErrore(messaggio(e, 'Errore nella riarchiviazione'));
        } finally {
            setInCorso(false);
        }
    };

    const conferma = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            const r = await api.post(
                `/firma-tablet/anagrafiche/${aperta.id}/conferma`,
                {
                    dati: modifiche,
                    su_paziente_id: suEsistente ? aperta.esistente_id : null,
                }
            );
            setAvviso(r.data.message);
            setAperta(null);
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella conferma'));
        } finally {
            setInCorso(false);
        }
    };

    const scarta = async () => {
        const motivo = window.prompt(
            'Perché questa accettazione va scartata?\n(resta registrata, non viene cancellata)'
        );
        if (motivo === null) return;
        try {
            setInCorso(true);
            await api.post(`/firma-tablet/anagrafiche/${aperta.id}/scarta`,
                           { motivo: motivo || 'Scartata dalla segreteria' });
            setAvviso('Accettazione scartata');
            setAperta(null);
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nello scarto'));
        } finally {
            setInCorso(false);
        }
    };

    if (caricamento && !anagrafiche.length) {
        return <div className="acc-attesa">Caricamento…</div>;
    }

    return (
        <div className="acc-wrap">
            <div className="acc-testata">
                <h1><UserCheck size={22} /> Accettazioni dal tablet</h1>
                {/* Il posto dove uno si accorge che un tablet e' fermo e'
                    questa schermata, non Amministrazione: il collegamento
                    sta qui, dove ci proverebbe chi sta al banco. */}
                <Link to="/dispositivi" className="acc-link-tablet">
                    Tablet fermo? Dispositivi e compilazioni in sospeso →
                </Link>
                <button className="acc-btn" onClick={carica}>
                    <RefreshCw size={15} /> Aggiorna
                </button>
            </div>

            {/* Sta sempre a schermo, anche a coda piena: senza, chi trova
                due righe dove prima ne trovava venti pensa che le altre
                siano andate perse. */}
            <p className="acc-nota acc-nota--testata">
                Le accettazioni complete compilate sul tablet entrano in archivio da
                sole. Qui restano quelle del punto prelievi e le pratiche che non si
                sono potute chiudere.
            </p>

            {errore && (
                <div className="acc-errore">
                    <AlertTriangle size={16} /> {errore}
                    <button onClick={() => setErrore(null)}><X size={14} /></button>
                </div>
            )}
            {avviso && (
                <div className="acc-avviso">
                    <Check size={16} /> {avviso}
                    <button onClick={() => setAvviso(null)}><X size={14} /></button>
                </div>
            )}

            {anagrafiche.length === 0 ? (
                <div className="acc-vuoto">
                    <p>Nessuna accettazione in attesa.</p>
                    <p className="acc-nota">
                        Le accettazioni complete compilate sul tablet diventano una
                        scheda da sole, senza passare di qui. Compare qui solo quello
                        che serve guardare: le accettazioni del punto prelievi, le
                        pratiche che non si sono potute chiudere da sole, e quelle
                        il cui paziente è nato ma senza tutti i moduli firmati. Il
                        motivo è scritto su ogni riga.
                    </p>
                </div>
            ) : (
                <div className="acc-elenco">
                    {anagrafiche.map((a) => (
                        <button key={a.id} className="acc-riga" onClick={() => apri(a)}>
                            <div className="acc-riga-nome">
                                <b>{a.cognome} {a.nome}</b>
                                <span className="acc-sub">
                                    {a.codice_fiscale || 'senza codice fiscale'}
                                    {a.data_nascita ? ` · ${dataIta(a.data_nascita)}` : ''}
                                </span>
                                {/* Il motivo sta sulla riga, non dentro: una coda di
                                    righe uguali si apre una per una per scoprire
                                    perché sono lì, ed è il lavoro che questa
                                    schermata dovrebbe togliere. */}
                                {a.motivo_attesa && (
                                    <span className="acc-motivo">
                                        <AlertTriangle size={12} /> {a.motivo_attesa}
                                    </span>
                                )}
                            </div>
                            <div className="acc-riga-lato">
                                {a.stato === 'archiviazione_non_riuscita' && (
                                    <span className="acc-etichetta acc-etichetta--guasto">
                                        archiviazione non riuscita
                                    </span>
                                )}
                                {a.tipo_accettazione === 'prelievi' && (
                                    <span className="acc-etichetta acc-etichetta--prelievi">
                                        punto prelievi
                                    </span>
                                )}
                                {a.esistente_id && (
                                    <span className="acc-etichetta acc-etichetta--noto">
                                        già in archivio: {a.esistente_codice}
                                    </span>
                                )}
                                <span className="acc-sub">
                                    <IconaTablet size={13} /> {a.dispositivo || '—'} · {dataOra(a.created_at)}
                                </span>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {/* ---------- verifica ---------- */}
            {aperta && (
                <div className="acc-modale-sfondo" onClick={chiudi}>
                    <div className="acc-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="acc-modale-testata">
                            <span>
                                <Pencil size={17} /> Verifica dei dati
                                {aperta.tipo_accettazione === 'prelievi' && ' — punto prelievi'}
                            </span>
                            <button onClick={chiudi}><X size={18} /></button>
                        </div>

                        <div className="acc-modale-corpo">
                            {daRiarchiviare && (
                                <div className="acc-guasto">
                                    <AlertTriangle size={16} />
                                    <div>
                                        <b>La scheda del paziente è stata creata, ma non
                                        tutti i moduli firmati sono finiti in archivio.</b>
                                        {' '}{aperta.motivo_attesa}{' '}
                                        La firma raccolta sul tablet è conservata apposta:
                                        «Riprova archiviazione» rifà <b>solo</b> i moduli
                                        che mancano e non crea un secondo paziente.
                                        Premerlo due volte non fa danni.
                                        {aperta.esistente_id && (
                                            <> La scheda è{' '}
                                                <Link to={`/pazienti/${aperta.esistente_id}`}>
                                                    {aperta.esistente_codice}
                                                </Link>.
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {aperta.esistente_id && !daRiarchiviare && (
                                <div className="acc-noto">
                                    <AlertTriangle size={16} />
                                    <div>
                                        Questo codice fiscale appartiene già a{' '}
                                        <b>{aperta.esistente_cognome} {aperta.esistente_nome}</b>{' '}
                                        ({aperta.esistente_codice}).{' '}
                                        {suEsistente
                                            ? 'I moduli firmati vengono archiviati in quella '
                                              + 'scheda: non ne nasce una nuova. I consensi '
                                              + 'dello stesso tipo già presenti restano nello '
                                              + 'storico, ma da questo momento valgono questi. '
                                              + 'Se invece il codice fiscale scritto al tablet '
                                              + 'è sbagliato, correggilo qui sotto: la scheda '
                                              + 'tornerà a essere nuova.'
                                            : 'Confermando, la scheda esistente viene '
                                              + 'aggiornata: non ne nasce una nuova.'}
                                    </div>
                                </div>
                            )}

                            {/* Gli altri motivi per cui una pratica è rimasta qui:
                                documento non valido, un dato mancante, una firma
                                illeggibile. Quello del codice fiscale ha già il suo
                                riquadro sopra, con più contesto. */}
                            {aperta.motivo_attesa && !aperta.esistente_id && !daRiarchiviare && (
                                <div className="acc-noto">
                                    <AlertTriangle size={16} />
                                    <div>{aperta.motivo_attesa}</div>
                                </div>
                            )}

                            {errore && (
                                <div className="acc-errore">
                                    <AlertTriangle size={16} /> {errore}
                                </div>
                            )}

                            {!daRiarchiviare && (
                                <p className="acc-nota">
                                    Correggi quello che serve prima di confermare: i dati
                                    sono stati scritti dal paziente.
                                </p>
                            )}

                            {!daRiarchiviare && (
                            <div className="acc-griglia">
                                {CAMPI.map(([campo, etichetta]) => (
                                    <label key={campo} className="acc-campo">
                                        <span>{etichetta}</span>
                                        {campo === 'luogo_nascita' ? (
                                            <RicercaComune
                                                valore={valore(campo)}
                                                codiceFiscale={valore('codice_fiscale')}
                                                statoEstero={valore('stato_nascita')}
                                                onChange={(v) => setModifiche({ ...modifiche, luogo_nascita: v })}
                                                onScelta={(c) => setModifiche({
                                                    ...modifiche,
                                                    luogo_nascita: c.nome,
                                                    provincia_nascita: c.sigla_provincia,
                                                })}
                                            />
                                        ) : campo === 'codice_fiscale' ? (
                                            <CampoDocumento
                                                valore={valore('codice_fiscale')}
                                                tipo={modifiche.tipo_documento || aperta.tipo_documento || 'cf_italiano'}
                                                onChange={(v) => setModifiche({ ...modifiche, codice_fiscale: v })}
                                                onTipoChange={(v) => setModifiche({ ...modifiche, tipo_documento: v })}
                                                anagrafica={{
                                                    data_nascita: valore('data_nascita'),
                                                    sesso: valore('sesso'),
                                                }}
                                            />
                                        ) : campo === 'citta' ? (
                                            <RicercaComune
                                                valore={valore(campo)}
                                                onChange={(v) => setModifiche({ ...modifiche, citta: v })}
                                                onScelta={(c) => setModifiche({
                                                    ...modifiche,
                                                    citta: c.nome,
                                                    provincia: c.sigla_provincia,
                                                    cap: valore('cap') || c.cap || '',
                                                })}
                                            />
                                        ) : campo === 'data_nascita' ? (
                                            <CampoDataNascita
                                                valore={String(valore(campo) || '').slice(0, 10)}
                                                onChange={(v) => setModifiche({ ...modifiche, data_nascita: v })}
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                value={valore(campo)}
                                                onChange={(e) => setModifiche({
                                                    ...modifiche, [campo]: e.target.value
                                                })}
                                            />
                                        )}
                                    </label>
                                ))}
                                <AvvisoCap cap={valore('cap')} comune={valore('citta')}
                                           attivo={!String(valore('stato_residenza') || '').trim() || /^ital/i.test(valore('stato_residenza'))} />
                            </div>
                            )}

                            {aperta.firma_base64 && (
                                <div className="acc-firma">
                                    <span>Firma raccolta sul tablet</span>
                                    <img src={aperta.firma_base64} alt="Firma del paziente" />
                                </div>
                            )}

                            <div className="acc-modale-azioni">
                                {/* «Scarta» non compare quando il paziente è già
                                    stato creato: scartare non lo toglierebbe, e
                                    direbbe il falso sulla riga. */}
                                {!daRiarchiviare && (
                                    <button className="acc-btn acc-btn--scarta"
                                            onClick={scarta} disabled={inCorso}>
                                        Scarta
                                    </button>
                                )}
                                <button className="acc-btn" onClick={chiudi}>
                                    Chiudi
                                </button>
                                {daRiarchiviare ? (
                                    <button className="acc-btn acc-btn--principale"
                                            onClick={riprova} disabled={inCorso}>
                                        <RefreshCw size={16} />
                                        Riprova archiviazione
                                    </button>
                                ) : (
                                    <button className="acc-btn acc-btn--principale"
                                            onClick={conferma} disabled={inCorso}>
                                        <Check size={16} />
                                        {suEsistente
                                            ? 'Archivia sulla scheda esistente'
                                            : aperta.esistente_id
                                                ? 'Aggiorna la scheda'
                                                : 'Crea il paziente'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccettazioniTablet;
